import React, { useEffect, useState } from 'react'
import { Panoramic } from '../../core/maps'

const CENTER_POSITION = {
  lat: 41.9405,
  lng: 2.3163,
}

const DEFAULT_POV = {
  heading: 0,
  pitch: 0,
}

/**
 * @typedef {object} Position
 * @property {number} lat - La latitud de la posición.
 * @property {number} lng - La longitud de la posición.
 * @property {object} [pixel] - Información de pixel adicional.
 */

/**
 * @typedef {object} POV
 * @property {number} heading - La dirección de la vista (0-360 grados).
 * @property {number} pitch - La inclinación de la vista (-90 a 90 grados).
 * @property {number} povLat - La latitud de la posición del panorama.
 * @property {number} povLng - La longitud de la posición del panorama.
 */

/**
 * @typedef {object} PanoramaPickerProps
 * @property {Position} position - La posición actual del marcador.
 * @property {function(Position): void} setPosition - La función callback para actualizar la posición cuando se mueve el marcador.
 * @property {Position} [center=CENTER_POSITION] - La posición central inicial del panorama (Barcelona por defecto).
 * @property {number} [heading=0] - La dirección inicial de la vista.
 * @property {number} [pitch=0] - La inclinación inicial de la vista.
 * @property {function(POV): void} setPov - La función callback para actualizar el punto de vista cuando cambia.
 */

/**
 * Un componente de selector de ubicación usando Google Street View Panorama.
 * 
 * Renderiza una vista de Street View con un marcador arrastrable personalizado (icono de tubería)
 * que permite al usuario seleccionar una ubicación precisa. Captura tanto la posición del marcador
 * como el punto de vista (dirección y ángulo) del panorama.
 * 
 * **Nota:** Este componente requiere que Google Maps API con Street View esté cargada y disponible globalmente.
 * 
 * @component
 * @example
 * const MyPanoramaPicker = () => {
 *   const [position, setPosition] = useState({ lat: 41.9405, lng: 2.3163 });
 *   const [pov, setPov] = useState({ heading: 0, pitch: 0 });
 * 
 *   return (
 *     <div style={{ height: '500px', width: '100%' }}>
 *       <PanoramaPicker
 *         position={position}
 *         setPosition={setPosition}
 *         center={{ lat: 41.9405, lng: 2.3163 }}
 *         heading={0}
 *         pitch={0}
 *         setPov={setPov}
 *       />
 *     </div>
 *   );
 * };
 * 
 * @param {PanoramaPickerProps} props - Las props del componente PanoramaPicker.
 */
export const PanoramaPicker = ({
  position,
  setPosition,
  center = CENTER_POSITION,
  heading = DEFAULT_POV.heading,
  pitch = DEFAULT_POV.pitch,
  setPov,
}) => {
  const [marker, setMarker] = useState()

  useEffect(() => {
    if (marker) {
      marker.setPosition(position)
    }
  }, [position]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Panoramic
      id="test"
      options={{
        position: center,
        pov: { heading, pitch },
        preference: 'nearest',
        zoomControl: false,
        scrollwheel: false,
        disableDoubleClickZoom: true,
      }}
      onMapLoad={(map) => {
        const gasIcon = {
          url: '/images/pipe_icon.svg',
          anchor: new window.google.maps.Point(40, 56),
          scaledSize: new window.google.maps.Size(80, 80),
        }
        const marker = new window.google.maps.Marker({
          position: position || center,
          map,
          draggable: true,
          icon: gasIcon,
        })
        setMarker(marker)
        setPosition(center)
        setPov({ ...map.pov, povLat: map.position.lat(), povLng: map.position.lng() })
        window.google.maps.event.addListener(marker, 'dragend', (e) => {
          setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng(), pixel: e.pixel })
        })
        window.google.maps.event.addListener(map, 'pov_changed', () => {
          setPov({ ...map.pov, povLat: map.position.lat(), povLng: map.position.lng() })
        })
      }}
    />
  )
}

export default PanoramaPicker
